import { Helmet } from 'react-helmet-async';
// @mui
import {
    Card,
    Stack,
    Container,
    Typography,
    TableContainer,
    CardContent,
    Button,
    CircularProgress,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
} from '@mui/material';

import {useNavigate, useParams} from 'react-router-dom';

// components
import Scrollbar from '../components/scrollbar';
// sections
import { StepByStepTable } from "../sections/@dashboard/attestations";
import {GeneralLayout} from "../layouts/GeneralLayout";
import Label from "../components/label";
import TextField from "@mui/material/TextField";
import {useEffect, useState} from "react";
import {useGetNextAttestationNumberQuery, usePostCreateAttestationMutation} from "../store/attestation/attestationApi";
import PageTitle from "../components/PageTitle";
import MenuItem from "@mui/material/MenuItem";
import {useGetFacultiesQuery, useLazyGetGroupsByFacultyQuery} from "../store/faculty/facultyApi";
import copyObject from "../utils/copyObject";
import {CheckBox, SaveOutlined} from "@mui/icons-material";
import {useGetStudentsInGroupQuery} from "../store/group/groupApi";
import {useSaveMedicalGroupsMutation} from "../store/student/studentApi";

export default function MedicalCommissionManagementPage() {
    const {id} = useParams();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const {data: facultiesData} = useGetFacultiesQuery();
    const [facultiesList, setFacultiesList] = useState([]);
    const [studentsList, setStudentsList] = useState([]);
    const [selectedFacultyId, setSelectedFacultyId] = useState(null);
    const [getGroupsQuery] = useLazyGetGroupsByFacultyQuery();
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const {data: studentsData} = useGetStudentsInGroupQuery({groupId: selectedGroupId});
    const [refresh, setRefresh] = useState(false);
    const [saveMedicalGroups] = useSaveMedicalGroupsMutation();

    const update = (idx, patch) =>
        setStudentsList(prev =>
            prev.map((s, i) => (i === idx ? { ...s, ...patch } : s))
        );

    useEffect(() => {
        if (facultiesData !== null && facultiesData !== undefined && facultiesList.length === 0) {
            setFacultiesList(copyObject(facultiesData.data).sort((l,r) => l.value > r.value ? 1 : -1));
        }

        if (studentsData !== null && studentsData !== undefined) {
            setStudentsList(copyObject(studentsData.data).sort((l,r) => l.value > r.value ? 1 : -1));
        }
    }, [facultiesData, studentsData]);

    useEffect(() => {
        setStudentsList([]);          // wipe old screen
    }, [selectedGroupId]);

    function formatDate(iso) {
        if (!iso) return "";
        const d = new Date(iso);
        return Number.isNaN(d) ? ""
            : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }

    function toDateString(date){
        if (date === null || date === undefined)
            return '';

        return new Date(date).toLocaleDateString("ru-RU");
    }

    function withZero(num) {
        return num < 10 ? `0${num}` : num;
    }
    
    async function handleFacultyChange(event) {
        setSelectedFacultyId(event.target.value);
        const request = {
            facultyId: parseInt(event.target.value, 10)
        };
        console.log(request);

        await getGroupsQuery({data: request}).unwrap().then((fulfilled) => {
            if (fulfilled?.success) {
                setGroups(copyObject(fulfilled.data).sort((l,r) => l.value > r.value ? 1 : -1))
            } else {
                fulfilled.errors.map((item) => {
                    alert(item);
                    return 1;
                });
            }

        }).catch((e) => {
            alert('Ошибка');
        });
    }

    const handleSave = async () => {
        setIsSaving(true);

        await saveMedicalGroups({data: {students: studentsList}}).unwrap().then((fulfilled) => {
            if (fulfilled?.success) {
                setIsSaving(false);
                alert("Данные успешно сохранены");
            } else {
                fulfilled.errors.map((item) => {
                    alert(item);
                    return 1;
                });

                setIsSaving(false);
            }
        }).catch(() => {
            setIsSaving(false);
            alert('Ошибка');
        });
    };
    
    function applyVisualChanges() {
        setRefresh(!refresh);
    }

    return (
        <GeneralLayout>
            <PageTitle title={`Допуски студентов по медкомиссии`} />

            <Container>
                <Stack direction="column"
                       padding="120px 0px 0px 100px"
                       spacing={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                        <Typography variant="h4" gutterBottom>
                            {facultiesData === null || facultiesData === undefined ? <CircularProgress/> : <></>}
                            Допуски студентов по медкомиссии
                        </Typography>
                    </Stack>

                    <Card>
                        <CardContent>
                            <Stack spacing={2} direction={'row'}>
                                <TextField select
                                           fullWidth
                                           onChange={(event) => handleFacultyChange(event)}
                                           label={'Факультет'}
                                           size={'small'}>
                                    {facultiesList?.map((item) => {
                                        return <MenuItem key={item.value} value={item.key}>{item.value}</MenuItem>
                                    })}
                                </TextField>

                                <TextField select
                                           fullWidth
                                           value={selectedGroupId}
                                           onChange={(event) => setSelectedGroupId(event.target.value)}
                                           label={'Группа'}
                                           size={'small'}>
                                    {groups?.map((item) => {
                                        return <MenuItem key={item.value} value={item.key}>{item.value}</MenuItem>
                                    })}
                                </TextField>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>№</TableCell>
                                    <TableCell>ФИО</TableCell>
                                    <TableCell align={'center'}>Отметка о прохождении медкомиссии</TableCell>
                                    <TableCell>Дата прохождения</TableCell>
                                    <TableCell>Мед. группа</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {studentsList.map((item, index) => {
                                    return <TableRow>
                                        <TableCell><Typography fontSize={'large'}>{index + 1}</Typography></TableCell>
                                        <TableCell>{item.value}</TableCell>
                                        <TableCell align={'center'}>
                                            <Checkbox checked={item.medicalCommissionPassed}
                                                      onChange={(_, v) => update(index, { medicalCommissionPassed: v })}/>
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="date"
                                                value={formatDate(item.medicalCommissionExaminationDate)}   // ← no new Date() here
                                                onChange={e => {
                                                    const raw = e.target.value;
                                                    update(index, { medicalCommissionExaminationDate: raw || null });
                                                }}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField size={'small'}
                                                       value={item.medicalGroup}
                                                       onChange={e => update(index, { medicalGroup: e.target.value })}/>
                                        </TableCell>
                                    </TableRow>
                                })}
                            </TableBody>
                        </Table>

                        <br/>
                        <Button variant={'contained'}
                                onClick={() => handleSave()}
                                disabled={isSaving || studentsList.length === 0}
                                startIcon={isSaving ? <CircularProgress size={15}/> : <SaveOutlined />}
                                fullWidth>Сохранить</Button>
                    </Card>

                    <br/>
                    <br/>
                </Stack>
            </Container>
        </GeneralLayout>
    );
}
